import React, { useState } from "react";
import {
  Button,
  Header,
  Icon,
  Label,
  List,
  Modal,
  Grid,
  Divider,
} from "semantic-ui-react";
import axios from "../../../../../../apis/dvdlocator";
import { useDispatch } from "react-redux";
import { addNotification } from "../../../../../../redux/actions/notificationActions";
import AddFamilySelect from "./AddFamilySelect";
import DeleteFamilySelect from "./DeleteFamilySelect";
import FamilyPanel from "../_SharedFamilyComponents/FamilyPanel";

const FamilyManagement = ({ open, close, initialUnits }) => {
  const dispatch = useDispatch();
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [inventory, setInventory] = useState(initialUnits);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [addingAll, setAddingAll] = useState(false);
  const [deleteSelection, setDeleteSelection] = useState({
    product: null,
    family: null,
  });
  const [deleteHoverId, setDeleteHoverId] = useState(null);
  const [familyHoverId, setFamilyHoverId] = useState(null);
  const [familyToView, setFamilyToView] = useState({});
  const [modal, setModal] = useState("");

  const openFamilyPanel = (family) => {
    setFamilyToView(family);
    setModal("familyPanel");
  };

  const getAllUniqueProductFamilies = () => {
    const families = [];

    inventory.forEach((unit) => {
      unit.product_families.forEach((fam) => {
        const id = fam.id;

        if (!families.some((entry) => entry.id === id)) families.push(fam);
      });
    });

    return families;
  };

  const addProductsToFamily = async (familyId, familyName) => {
    let upcs = addingAll
      ? inventory.map((unit) => unit.supply.upc)
      : [selectedUnit.supply.upc];

    // Filtering out inventory that already have this family
    upcs = upcs.filter((id) => {
      const unit = inventory.filter((row) => row.supply.upc === id)[0];
      if (unit.product_families.some((fam) => fam.id === familyId))
        return false;
      return true;
    });

    if (upcs.length < 1) {
      dispatch(
        addNotification({
          text: `Product(s) already belong to family.`,
          icon: <Icon color="yellow" name="warning circle" />,
        })
      );
      return setModal("");
    }

    try {
      await axios.post(`/families/${familyId}/supply`, { upcs });

      setInventory((prev) =>
        prev.map((unit) => {
          if (upcs.includes(unit.supply.upc))
            return {
              ...unit,
              product_families: [
                ...unit.product_families,
                { id: familyId, family: familyName },
              ],
            };
          else return unit;
        })
      );

      dispatch(
        addNotification({
          text: addingAll
            ? `Added family to all inventory!`
            : `Added family to product!`,
          icon: <Icon color="green" name="checkmark box" />,
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(
        addNotification({
          text: `Error adding family to product(s).`,
          icon: <Icon color="red" name="warning circle" />,
        })
      );
    } finally {
      setModal("");
    }
  };

  const removeFamilyAll = async (familyId) => {
    const upcs = inventory
      .filter((unit) =>
        unit.product_families.some((fam) => fam.id === familyId)
      )
      .map((unit) => unit.supply.upc);

    try {
      for (const upc of upcs) {
        await axios.delete(`/families/${familyId}/supply/${upc}`);
      }

      setInventory((prev) =>
        prev.map((item) => {
          if (upcs.includes(item.supply.upc))
            return {
              ...item,
              product_families: item.product_families.filter(
                (fam) => fam.id !== familyId
              ),
            };
          else return item;
        })
      );

      dispatch(
        addNotification({
          text: "Removed family from inventory!",
          icon: <Icon color="green" name="checkmark box" />,
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(
        addNotification({
          text: `Error removing family from inventory.`,
          icon: <Icon color="red" name="warning circle" />,
        })
      );
    } finally {
      setModal("");
    }
  };

  const removeProductFromFamily = async () => {
    setDeleteSubmitting(true);
    const upc = deleteSelection.unit.supply.upc;
    const familyId = deleteSelection.family.id;

    try {
      await axios.delete(`/families/${familyId}/supply/${upc}`);

      setInventory((prev) =>
        prev.map((item) => {
          if (item.supply.upc === upc)
            return {
              ...item,
              product_families: item.product_families.filter(
                (fam) => fam.id !== familyId
              ),
            };
          else return item;
        })
      );

      dispatch(
        addNotification({
          text: `Removed family from product(s)!`,
          icon: <Icon color="green" name="checkmark box" />,
        })
      );
    } catch (err) {
      dispatch(
        addNotification({
          text: `Error removing family from product(s).`,
          icon: <Icon color="red" name="warning circle" />,
        })
      );
    } finally {
      setModal("");
      setDeleteSubmitting(false);
    }
  };

  const deleteFamilyFromState = (id) => {
    setInventory((prev) =>
      prev.map((unit) => ({
        ...unit,
        product_families: unit.product_families.filter((fam) => fam.id !== id),
      }))
    );
  };

  const updateFamilyName = (id, familyName) => {
    setFamilyToView({ family: familyName, familyName, id: id, familyId: id });

    setInventory((prev) =>
      prev.map((unit) => {
        if (!unit.product_families.some((fam) => fam.id === id)) return unit;

        return {
          ...unit,
          product_families: unit.product_families.map((fam) => {
            if (fam.id === id) return { ...fam, family: familyName };

            return fam;
          }),
        };
      })
    );
  };

  if (modal === "add")
    return (
      <AddFamilySelect
        open={modal === "add"}
        close={() => setModal("")}
        addingAll={addingAll}
        addProductsToFamily={addProductsToFamily}
      />
    );

  if (modal === "deleteAll")
    return (
      <DeleteFamilySelect
        open={modal === "deleteAll"}
        families={getAllUniqueProductFamilies()}
        close={() => setModal("")}
        remove={removeFamilyAll}
      />
    );

  if (modal === "delete")
    return (
      <Modal open={modal === "delete"} size="tiny">
        <Modal.Header>Remove family association</Modal.Header>
        <Modal.Content>
          <p>
            Would you like to remove family:{" "}
            <strong>
              {deleteSelection.family && deleteSelection.family.family}
            </strong>{" "}
            from product:{" "}
            <strong>
              {deleteSelection.unit && deleteSelection.unit.supply.supply_title}
            </strong>
            ?
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={() => setModal("")}>Cancel</Button>
            <Button
              color="red"
              disabled={deleteSubmitting}
              onClick={removeProductFromFamily}
            >
              Delete
            </Button>
          </div>
        </Modal.Content>
      </Modal>
    );

  if (modal === "familyPanel")
    return (
      <FamilyPanel
        close={() => setModal("")}
        open={modal === "familyPanel"}
        family={familyToView}
        updateFamilyName={updateFamilyName}
        deleteFamilyFromState={deleteFamilyFromState}
      />
    );

  return (
    <Modal open={open} onClose={close} size="small">
      <Modal.Header>
        <Grid columns={2}>
          <Grid.Column floated="left" verticalAlign="middle">
            <Header floated="left">Edit Product Families</Header>
          </Grid.Column>
          <Grid.Column floated="right">
            <Grid columns={2}>
              <Grid.Column>
                <Button
                  icon
                  size="small"
                  color="red"
                  labelPosition="left"
                  title="Remove all inventory from a family"
                  onClick={() => {
                    setModal("deleteAll");
                  }}
                >
                  <Icon name="minus square" />
                  Family All
                </Button>
              </Grid.Column>
              <Grid.Column>
                <Button
                  icon
                  size="small"
                  color="green"
                  labelPosition="left"
                  title="Add all inventory to a family"
                  onClick={() => {
                    setModal("add");
                    setAddingAll(true);
                  }}
                >
                  <Icon name="plus square" />
                  Family All
                </Button>
              </Grid.Column>
            </Grid>
          </Grid.Column>
        </Grid>
      </Modal.Header>

      <Modal.Content scrolling>
        <List relaxed divided>
          {inventory.map((unit, i) => (
            <List.Item
              key={unit.supply.upc}
              style={
                i % 2 === 0
                  ? { backgroundColor: "#e8e8e8", padding: "1em" }
                  : { padding: "1em" }
              }
            >
              <Grid columns={2}>
                <Grid.Column width={12}>
                  <List.Content>
                    <p>
                      <strong>Title:</strong> {unit.supply.supply_title}
                    </p>

                    <div>
                      <strong style={{ paddingRight: "8px" }}>Families:</strong>{" "}
                      {unit.product_families.map((family) => (
                        <Label
                          size="medium"
                          className={`${
                            familyHoverId ===
                              `family-label-${unit.supply.upc}-${family.id}` &&
                            "family-label-hovered"
                          }`}
                          key={family.id}
                          style={{
                            backgroundColor: "white",
                            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                            marginRight: "8px",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <span
                              onClick={() => openFamilyPanel(family)}
                              id={`family-label-${unit.supply.upc}-${family.id}`}
                              onMouseEnter={(e) =>
                                setFamilyHoverId(e.target.id)
                              }
                              onMouseLeave={() => setFamilyHoverId(null)}
                              title="View family panel and options"
                              style={{ cursor: "pointer" }}
                            >
                              {family.family}
                            </span>
                            <Icon
                              circular
                              id={`delete-icon-${unit.supply.upc}-${family.id}`}
                              name="trash"
                              color="red"
                              title="Remove this unit from this family"
                              onMouseEnter={(e) =>
                                setDeleteHoverId(e.target.id)
                              }
                              onMouseLeave={() => setDeleteHoverId(null)}
                              inverted={
                                deleteHoverId !==
                                `delete-icon-${unit.supply.upc}-${family.id}`
                              }
                              style={{ cursor: "pointer", marginLeft: "8px" }}
                              onClick={() => {
                                setDeleteSelection({ family, unit });
                                setModal("delete");
                              }}
                            />
                          </div>
                        </Label>
                      ))}
                    </div>
                  </List.Content>
                </Grid.Column>
                <Grid.Column floated="right" verticalAlign="middle" width={4}>
                  <Button
                    icon
                    labelPosition="left"
                    floated="right"
                    size="tiny"
                    color="green"
                    title="Add this unit to a family"
                    onClick={() => {
                      setModal("add");
                      setSelectedUnit(unit);
                      setAddingAll(false);
                    }}
                  >
                    <Icon name="plus square" />
                    Family
                  </Button>
                </Grid.Column>
              </Grid>
            </List.Item>
          ))}
        </List>
      </Modal.Content>
      <Divider />
      <Modal.Content>
        <Grid columns={2}>
          <Grid.Column>
            <Button onClick={close}>Cancel</Button>
          </Grid.Column>

          <Grid.Column>
            <Button primary floated="right" onClick={close}>
              Done
            </Button>
          </Grid.Column>
        </Grid>
      </Modal.Content>
    </Modal>
  );
};

export default FamilyManagement;
